/**
 * featureScopedLabel — single source of truth for resolving an atom's display
 * label inside a feature scope.
 *
 * The serialization layer (`serializeFeatures`) already populates
 * `SerializedFeature.atomLabels` per feature — this module is the canonical
 * way to read those labels. All alt-text systems (HTML accessibility tree,
 * LLM input text block, arrow descriptions, stereo blocks) call through one
 * of the functions here.
 *
 * Per-feature semantics: the same atom can have a different label in different
 * features. A carbon shared by a 5-carbon chain and an alcohol reads as
 * "Carbon-3" inside the chain's scope and just "Carbon" inside the alcohol's
 * scope (where it's the only carbon).
 *
 * Default-feature fallback: when a callsite has no feature context the
 * resolver picks the atom's most-discriminating primary feature, ranked by
 * type priority. This keeps a stable name for the atom in workspace-summary
 * lines, arrow descriptions, and other feature-agnostic contexts.
 */
import {nameAtom} from "./nameAtom";
import type {FeatureType, SerializationResult, SerializedAtom, SerializedFeature} from "../serialization/SerializedFeature";

/**
 * Resolver bound to one `SerializationResult`. Returns a per-feature label
 * when `featureContextId` is in the atom's feature membership; otherwise
 * returns the default-feature label (priority rule).
 */
export type AtomLabelResolver = (atomIndex: number, featureContextId?: string) => string;

/**
 * Default-feature priority. A higher score wins when an atom belongs to
 * multiple features. Mirrors the `PRIMARY_FEATURE_TYPES` set used elsewhere
 * but with explicit ordering.
 */
const FEATURE_TYPE_PRIORITY: Record<FeatureType, number> = {
    ring: 6,
    bridged_bicyclic: 5,
    chain: 4,
    atom_cluster: 3,
    molecule: 2,
    functional_group: 1,
    atom: 0,
};

/**
 * O(1) lookup of the per-feature label parallel to `feature.atomLabels`.
 * Returns `undefined` when `atomIndex` is not in `featureId`'s atom list.
 */
export function featureLabel(
    serialized: SerializationResult,
    atomIndex: number,
    featureId: string,
): string | undefined {
    const feature = serialized.features.find(f => f.id === featureId);
    if (!feature) return undefined;
    const position = feature.atomIndices.indexOf(atomIndex);
    if (position === -1) return undefined;
    return feature.atomLabels[position];
}

/**
 * Resolve the atom's label using its default feature (highest type priority
 * among the features it belongs to). Used by callsites without a feature
 * context (workspace-summary lines, arrow endpoints, stereocenters).
 */
export function defaultFeatureLabel(
    serialized: SerializationResult,
    atomIndex: number,
): string {
    const atom = serialized.atoms.find(a => a.index === atomIndex);
    if (!atom) {
        return nameAtom("?");
    }

    const defaultFeature = pickDefaultFeature(atom, serialized);
    if (defaultFeature) {
        const label = featureLabel(serialized, atomIndex, defaultFeature.id);
        if (label !== undefined) return label;
    }

    // Defensive — shouldn't be reachable for atoms in `serialized.atoms`
    // since the serializer only emits atoms with at least one feature.
    return nameAtom(atom.symbol, atom.charge, atom.lonePairs);
}

/**
 * Build a resolver closure for one `SerializationResult`. Pre-indexes feature
 * lookups for O(1) access. Most callers want this — pass it through to
 * downstream description functions instead of a raw `Map<number, string>`.
 *
 * Resolution order:
 *   1. If `featureContextId` is supplied AND the atom is in that feature →
 *      that feature's per-feature label.
 *   2. Otherwise → the default-feature label (priority rule).
 *   3. Defensive fallback → `nameAtom(symbol, charge, lonePairs)`.
 */
export function makeAtomLabelResolver(serialized: SerializationResult): AtomLabelResolver {
    // Pre-index features by id for O(1) lookup.
    const featuresById = new Map<string, SerializedFeature>();
    for (const f of serialized.features) {
        featuresById.set(f.id, f);
    }

    // Pre-index atom-index → position-in-feature for every (feature, atom)
    // pair, so per-feature lookup avoids the indexOf scan.
    const positionByFeatureAndAtom = new Map<string, Map<number, number>>();
    for (const f of serialized.features) {
        const positions = new Map<number, number>();
        for (let i = 0; i < f.atomIndices.length; i++) {
            positions.set(f.atomIndices[i], i);
        }
        positionByFeatureAndAtom.set(f.id, positions);
    }

    // Pre-index atom-index → atom + cached default feature.
    const atomsByIndex = new Map<number, SerializedAtom>();
    const defaultFeatureByAtom = new Map<number, SerializedFeature | undefined>();
    for (const a of serialized.atoms) {
        atomsByIndex.set(a.index, a);
        defaultFeatureByAtom.set(a.index, pickDefaultFeature(a, serialized, featuresById));
    }

    return (atomIndex, featureContextId) => {
        if (featureContextId) {
            const positions = positionByFeatureAndAtom.get(featureContextId);
            if (positions) {
                const pos = positions.get(atomIndex);
                if (pos !== undefined) {
                    const feature = featuresById.get(featureContextId);
                    if (feature) return feature.atomLabels[pos];
                }
            }
        }

        const atom = atomsByIndex.get(atomIndex);
        if (!atom) return nameAtom("?");

        const defaultFeature = defaultFeatureByAtom.get(atomIndex);
        if (defaultFeature) {
            const positions = positionByFeatureAndAtom.get(defaultFeature.id);
            const pos = positions?.get(atomIndex);
            if (pos !== undefined) {
                return defaultFeature.atomLabels[pos];
            }
        }

        return nameAtom(atom.symbol, atom.charge, atom.lonePairs);
    };
}

function pickDefaultFeature(
    atom: SerializedAtom,
    serialized: SerializationResult,
    featuresById?: Map<string, SerializedFeature>,
): SerializedFeature | undefined {
    let best: SerializedFeature | undefined;
    let bestPriority = -1;
    for (const fid of atom.featureIds) {
        const f = featuresById?.get(fid) ?? serialized.features.find(x => x.id === fid);
        if (!f) continue;
        const priority = FEATURE_TYPE_PRIORITY[f.type] ?? -1;
        if (priority > bestPriority) {
            best = f;
            bestPriority = priority;
        }
    }
    return best;
}
